

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
    FormControlLabel,
    Switch,
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewItineraryDayModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        itinerary_id: '',
        day_number: '',
        title: '',
        description: '',
        image_url: '',
        inclusion: '',
        exclusion: '',
        guide_instructions: ''
    });

    const [itineraries, setItineraries] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
    }, [isOpen, axiosInstance]);

    const fetchData = async () => {
        setLoadingData(true);
        try {
            const itinerariesRes = await axiosInstance.get('/itineraries/');
            setItineraries(itinerariesRes.data);
        } catch (error) {
            console.error('Error fetching modal data:', error);
        } finally {
            setLoadingData(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const payload = {
                name: formData.title || `Itinerary Day ${formData.day_number || ''}`,
                itinerary_id: parseInt(formData.itinerary_id),
                day_number: formData.day_number ? parseInt(formData.day_number) : null,
                title: formData.title || null,
                description: formData.description || null,
                image_url: formData.image_url || null,
                inclusion: formData.inclusion || null,
                exclusion: formData.exclusion || null,
                guide_instructions: formData.guide_instructions || null,
            };

            await onSave(payload);
            onClose();
            // Reset form
            setFormData({
                itinerary_id: '',
                day_number: '',
                title: '',
                description: '',
                image_url: '',
                inclusion: '',
                exclusion: '',
                guide_instructions: ''
            });
        } catch (error) {
            console.error('Error saving Itinerary Day:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Itinerary Day',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '800px' },
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Itinerary Day</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4 }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Itinerary</InputLabel>
                                        <Select
                                            name="itinerary_id"
                                            value={formData.itinerary_id}
                                            onChange={handleInputChange}
                                            label="Itinerary"

                                            disabled={loadingData}
                                        >
                                            {itineraries.map(itinerary => (
                                                <MenuItem key={itinerary.id} value={itinerary.id}>
                                                    {itinerary.name || itinerary.title || `Itinerary ${itinerary.id}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Day Number"
                                        name="day_number"
                                        value={formData.day_number}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Itinerary Day Title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Image URL"
                                        name="image_url"
                                        value={formData.image_url}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        multiline
                                        rows={4}
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Inclusion"
                                        name="inclusion"
                                        multiline
                                        rows={4}
                                        value={formData.inclusion}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Exclusion"
                                        name="exclusion"
                                        multiline
                                        rows={4}
                                        value={formData.exclusion}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Guide Instructions"
                                        name="guide_instructions"
                                        multiline
                                        rows={4}
                                        value={formData.guide_instructions}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Itinerary Day'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewItineraryDayModal;
